import { useCallback, useRef, useState } from "react";

import useRecorder from "@features/session/hooks/useRecorder";

/**
 * Push-to-talk wrapper over the session useRecorder. `stop()` resolves with the
 * recorded file uri (or undefined if none), bridging useRecorder's
 * onRecordingComplete callback into a promise. `cancel()` discards.
 */
export const useProethicsRecorder = () => {
	const resolveRef = useRef<((uri: string | undefined) => void) | null>(null);
	const [isRecording, setIsRecording] = useState(false);

	const recorder = useRecorder({
		onRecordingComplete: (uri: string) => {
			const resolve = resolveRef.current;
			resolveRef.current = null;
			resolve?.(uri);
		},
	});

	const start = useCallback(async () => {
		// Reset the waveform so a new take doesn't show the previous one's levels.
		recorder.clearMeterHistory();
		await recorder.startRecording(0);
		setIsRecording(true);
	}, [recorder]);

	const stop = useCallback(
		(): Promise<string | undefined> =>
			new Promise((resolve) => {
				resolveRef.current = resolve;
				recorder.stopRecording().then(() => {
					setIsRecording(false);
					// If no uri was produced, the callback never fired - resolve undefined.
					if (resolveRef.current) {
						resolveRef.current = null;
						resolve(undefined);
					}
				});
			}),
		[recorder],
	);

	const cancel = useCallback(async () => {
		resolveRef.current = null;
		await recorder.unloadRecording();
		recorder.clearMeterHistory();
		setIsRecording(false);
	}, [recorder]);

	return {
		isRecording,
		// Live audio levels + elapsed time for the recording waveform/timer.
		meterHistory: recorder.meterHistory,
		durationMs: recorder.currentTime,
		start,
		stop,
		cancel,
	};
};
